import { useMutation } from "@apollo/client";
import { Form, Formik, FormikProps } from "formik";
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ImageGallery from "src/components/AddPictures/ImageGallery";
import UploadBox from "src/components/AddPictures/UploadBox";
import UploadingView from "src/components/AddPictures/Uploading";
import Button from "src/components/Button";
import Input from "src/components/Input";
import { AppContext, ValueProps } from "src/context";
import {
  CreateAsoebiAttire,
  UpdateAsoebiAttire,
  VerifyAseobiAttireUpload,
} from "src/graphql/mutations";
import { GetAsoEbiAttires } from "src/graphql/queries";
import { formatMoney, getFileDimension } from "src/helper/functions";
import useMediaUpload from "src/hooks/useMediaUpload";
import useAppStore from "src/store/utils";
import {
  AddAseobiAttire,
  AddAseobiAttireVariables,
  UpdateAseobiAttire,
  UpdateAseobiAttireVariables,
  VerifyAseobiAttireUploadVariables,
  VerifyAseobiAttireUpload as verifyAseobiAttireUpload,
} from "src/utils/gql_types";
import { UploadValue } from "src/utils/types";
import * as Yup from "yup";
import styles from "./styles.module.scss";

interface Values {
  name: string;
  price: string;
}

interface UploadedFile {
  preview: string;
  file: File;
  width?: number;
  height?: number;
}

const AddPartSchema = Yup.object().shape({
  name: Yup.string().required("Asoebi attire name is required"),
  price: Yup.number().required("Please input a price"),
});

interface Props {
  setOpen: Dispatch<SetStateAction<boolean>>;
  groupId: string | undefined;
}

function AddNewAttire({ setOpen, groupId }: Props) {
  const { event } = useAppStore((state) => state);
  const [step, setStep] = useState<number>(1);
  const [fileUploaded, setFileUploaded] = useState<UploadValue>([]);
  const [extraUpload, setExtraUpload] = useState<UploadValue>([]);
  const [submitting, setSubmitting] = useState(false);
  const [active, setActive] = useState("");
  const navigate = useNavigate();

  const contextValue = useContext<ValueProps | null>(AppContext);
  const asoebiFeePercentage = contextValue?.asoebiFeePercentage ?? 0;

  const {
    percentageCompleted,
    totalTimeLeft,
    isCompleted,
    handleUpload,
    uploadedAttireMedia,
    setCompleted,
  } = useMediaUpload();

  const [verifyAseobiAttireUpload] = useMutation<
    verifyAseobiAttireUpload,
    VerifyAseobiAttireUploadVariables
  >(VerifyAseobiAttireUpload, {
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">
            {error?.message ?? "An error occurred uploading attire image"}
          </p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const [attireId, setAttireId] = useState("");

  const [createAsoebiAttire, { loading: createAsoebiAttireLoading }] =
    useMutation<AddAseobiAttire, AddAseobiAttireVariables>(CreateAsoebiAttire, {
      onCompleted(data) {
        setAttireId(data.addAseobiAttire);
        // toast.success(
        //   <p className="toast">You have successfully created a group.</p>
        // );
        setStep(2);
      },
      onError(error) {
        if (error?.message !== "unauthenticated") {
          toast.error(
            <p className="toast">{error?.message ?? "An error occurred"}</p>
          );
        } else {
          navigate("/clear");
        }
      },
    });

  const [updateAsoebiAttire] = useMutation<
    UpdateAseobiAttire,
    UpdateAseobiAttireVariables
  >(UpdateAsoebiAttire, {
    onCompleted() {
      toast.success(<p className="toast">Image successfully added.</p>);
      onClose();
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">
            {error?.message ?? "An error occurred uploading attire image"}
          </p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  const onCloseModal = () => {
    setStep(1);
    setFileUploaded([]);
    setExtraUpload([]);
    setCompleted([]);
    setActive("");
  };

  const onClose = () => {
    setOpen(false);
    onCloseModal();
  };

  const onUpload = async (
    event: ChangeEvent<HTMLInputElement>,
    addMore: boolean = false
  ) => {
    const input = event.target;
    const files = event.target.files as FileList;
    if (!files) return;

    const filesArray = Array.from(files);

    if (!filesArray.length) {
      if (!addMore) setFileUploaded([]);
      return;
    }

    try {
      const uploadedFiles = await Promise.all(
        filesArray.map(async (file): Promise<UploadedFile> => {
          const preview = URL.createObjectURL(file);
          const { width, height } = await getFileDimension(file);
          return { preview, file, width, height };
        })
      );

      if (addMore) {
        setExtraUpload((prev: UploadedFile[]) => [...prev, ...uploadedFiles]);
      } else {
        setFileUploaded(uploadedFiles);
        handleUpload(uploadedFiles, "attire", attireId);
        setStep(3);
      }

      input.value = "";
    } catch (error) {
      console.error("Error processing files:", error);
      if (!addMore) setFileUploaded([]);
      toast.error("Failed to process files");
    }
  };

  const handleSubmit = async (values: Values) => {
    let errorOccured = false;
    setSubmitting(true);

    // const extraUploads: UploadAttireResult = [];
    const uploaded = uploadedAttireMedia.filter((item) =>
      fileUploaded.some((media) => media?.preview === item?.preview)
    );

    // if (extraUpload.length) {
    //   await Promise.all(
    //     extraUpload.map(async ({ file, preview, width, height }) => {
    //       try {
    //         const res = await uploadAttireMedia({
    //           variables: {
    //             event_id: event.uuid,
    //             attire_id: attireId,
    //             mediaType: file.type.split("/")[1],
    //           },
    //         });

    //         const presignedUrl =
    //           res.data?.generateAseobiAttirePresignUrl?.upload_url;
    //         const fileUri = res.data?.generateAseobiAttirePresignUrl?.file_uri;

    //         if (!presignedUrl) {
    //           throw new Error("Failed to get presigned URL");
    //         }

    //         await axios.put(presignedUrl, file, {
    //           headers: {
    //             "Content-Type": file.type,
    //             "x-amz-acl": "public-read",
    //           },
    //         });

    //         extraUploads.push({
    //           preview,
    //           width,
    //           height,
    //           fileUri: fileUri ?? "",
    //           attire_id: attireId,
    //         });
    //       } catch (err) {
    //         errorOccured = true;
    //         toast.error("An error occurred while uploading");
    //       }
    //     })
    //   );
    // }

    // const totalUploads = [...uploaded, ...extraUploads];
    const totalUploads = [...uploaded];
    await Promise.all(
      totalUploads.map(async ({ fileUri, height, width, attire_id }) => {
        try {
          await verifyAseobiAttireUpload({
            variables: {
              event_id: event.uuid,
              fileUri: `${fileUri}?height=${height}&width=${width}`,
              replace: false,
              attire_id,
            },
            refetchQueries: [
              {
                query: GetAsoEbiAttires,
                variables: {
                  event_id: event.uuid,
                  aseobi_id: groupId ?? "",
                },
              },
            ],
          });

          updateAsoebiAttire({
            variables: {
              event_id: event.uuid,
              id: attireId,
              input: {
                active: true,
                aseobi_id: groupId ?? "",
                currency_iso: "ngn",
                description: "",
                name: values.name,
                price_per_yard: parseInt(values.price),
                yards_available: 20,
                images: [fileUri],
              },
            },
          });
        } catch (err) {
          errorOccured = true;
          toast.error("An error occurred while verifying upload");
        }
      })
    );

    setSubmitting(false);
    if (!errorOccured) {
      toast.success("Attire added successfully!");
      onClose();
    }
  };

  useEffect(() => {
    if (isCompleted && fileUploaded?.length) {
      setActive([...fileUploaded, ...extraUpload][0]?.preview);
      setStep(4);
    }
  }, [isCompleted, fileUploaded, extraUpload]);

  return (
    <div className={styles["AddPart"]}>
      <div className={styles["AddPart-header"]}>
        <h1>Add new attire</h1>
        <span>
          A name that represents the specific people you're curating for.
        </span>
      </div>
      <Formik
        initialValues={{ name: "", price: "" }}
        validationSchema={AddPartSchema}
        onSubmit={handleSubmit}
      >
        {(props: FormikProps<Values>) => (
          <Form className={styles["AddPart-form"]}>
            {step === 1 && (
              <>
                <div className={styles["AddPart-form_container"]}>
                  <Input
                    type="text"
                    name="name"
                    label="Attire Name"
                    placeholder="e.g Sky blue 5 star cashmere fabric"
                  />
                  <div className="">
                    <Input
                      type="text"
                      name="price"
                      label="Price per yard"
                      placeholder="40,000"
                      money
                    />
                    <div className="mt-2 flex items-center justify-between pl-4 text-sm font-semibold text-gray-500">
                      <span className="">
                        {props.values.price
                          ? `+${formatMoney((parseInt(props.values.price) * asoebiFeePercentage) / 100)}`
                          : ""}
                      </span>
                      <span className="">
                        (+{asoebiFeePercentage}% Inawo service fee)
                      </span>
                    </div>
                  </div>
                </div>
                <div className={`flex items-center justify-between gap-4`}>
                  <Button
                    type="button"
                    label="Cancel"
                    design="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    label="Upload attire image"
                    design="primary"
                    onClick={() => {
                      createAsoebiAttire({
                        variables: {
                          event_id: event.uuid,
                          input: {
                            active: true,
                            aseobi_id: groupId ?? "",
                            currency_iso: "ngn",
                            description: "",
                            // images: [],
                            name: props.values.name,
                            price_per_yard: parseInt(props.values.price),
                            yards_available: 20,
                          },
                        },
                      });
                    }}
                    // disabled={createAsoebiAttireLoading}
                    disabled={
                      props.values.name === "" || props.values.price === ""
                    }
                    loader={createAsoebiAttireLoading}
                  >
                    Add image
                  </Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className={styles["AddPart-fileUpload"]}>
                  <label
                    className={`${styles["addPictures-image-box"]} cursor-pointer`}
                    htmlFor="add-file"
                  >
                    <UploadBox
                      handleBlur={props.handleBlur}
                      onUpload={onUpload}
                      isVideo={false}
                    />
                  </label>
                  {/* <div className={styles["AddPart-btnContainer"]}></div> */}
                </div>
                <Button
                  type="button"
                  label="Cancel"
                  design="secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
              </>
            )}

            {step === 3 && (
              <div className={styles["AddPart-fileUpload"]}>
                <div className={styles["addPictures-image-box"]}>
                  <UploadingView
                    fileUploaded={fileUploaded}
                    setFileUploaded={setFileUploaded}
                    setStep={setStep}
                    percentageCompleted={percentageCompleted}
                    totalTimeLeft={totalTimeLeft}
                    isCompleted={isCompleted}
                    setRetryUploads={() => handleUpload(fileUploaded, "attire")}
                    onClose={onCloseModal}
                  />
                </div>
              </div>
            )}

            {step === 4 && (
              <>
                <div className={styles["AddPart-fileUpload"]}>
                  <div className={styles["addPictures-image-box2"]}>
                    <ImageGallery
                      uploadResult={[...fileUploaded, ...extraUpload]}
                      active={active}
                      onRemoveImage={(preview) => {
                        const removeImage = (
                          uploads: UploadValue,
                          setUploads: Dispatch<SetStateAction<UploadValue>>
                        ) => {
                          const newArray = uploads.filter(
                            (item) => item?.preview !== preview
                          );
                          setUploads(newArray);
                        };

                        const allUploads = [...fileUploaded, ...extraUpload];

                        if (
                          fileUploaded.some((item) => item?.preview === preview)
                        ) {
                          removeImage(fileUploaded, setFileUploaded);
                        } else if (
                          extraUpload.some((item) => item?.preview === preview)
                        ) {
                          removeImage(extraUpload, setExtraUpload);
                        }

                        if (allUploads.length <= 1) setStep(2);

                        setActive(
                          allUploads.find((item) => item.preview !== preview)
                            ?.preview ?? ""
                        );
                      }}
                    />
                  </div>
                  {/* <div className="relative mb-8 flex h-[70px] w-full items-end">
                    <div className="group flex items-end overflow-x-auto">
                      {[...fileUploaded, ...extraUpload].map((img, index) => (
                        <div
                          key={index}
                          className={`-mr-5 h-[59px] w-[89px] max-w-[89px] flex-shrink-0 transition-all duration-300 ${
                            img?.preview === active
                              ? "z-10 h-[71px] w-[89px] filter-none"
                              : ""
                          } contrast-[60%] filter hover:z-10 hover:h-[71px] hover:w-[100px] hover:filter-none`}
                        >
                          {img?.file?.type?.includes("video/") ? (
                            <video
                              preload="none"
                              className="h-full w-full rounded-md object-cover transition-all duration-300"
                              onClick={() => setActive(img?.preview)}
                            >
                              <source src={img?.preview} type="video/mp4" />
                              Your browser does not support the video tag.
                            </video>
                          ) : (
                            <img
                              src={img?.preview}
                              alt={`Thumbnail ${index + 1}`}
                              className="h-full w-full rounded-md object-cover transition-all duration-300"
                              onClick={() => setActive(img?.preview)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <input
                      id="add-file"
                      multiple
                      hidden
                      type="file"
                      className={styles["file"]}
                      name="image"
                      accept="*"
                      onChange={(e) => onUpload(e, true)}
                      onBlur={props.handleBlur}
                    />
                    <label htmlFor="add-file">
                      <div
                        className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-md"
                        aria-label="add more images"
                        role="button"
                      >
                        <Addmore />
                      </div>
                    </label>
                  </div> */}
                </div>
                <div className={`flex items-center justify-between gap-4`}>
                  <Button
                    type="button"
                    label="Cancel"
                    design="secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    label="Add new attire"
                    design="primary"
                    disabled={submitting}
                    loader={submitting}
                  >
                    Submit
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default AddNewAttire;
